import React, {useCallback, useEffect, useState} from 'react'
import {useStaticQuery, graphql} from 'gatsby'
import Img from 'gatsby-image'
import {css} from '@emotion/react'
import {logDplWithExtensionParams, EVENTS} from '../../../util/dpl'
import {isChrome, isFirefox} from '../../../util/userAgent'
import {logSgc, setExtensionParamsToLocalStorage} from '../../../util/extensionMetadata'
import {colors} from '../../../constants'
import {createMarkup, convertDatoCmsImgPathsToLocal} from '../../../util/datoCmsDataFactory'
import {chromeProtectionExtensionLink, firefoxProtectionExtensionLink} from '../../../util/extensionStoreLinks'
import Button from './button'
import BrowserStoreWidget from './BrowserStoreWidget'
import SideBySide from '../../SideBySide'
import FaqWidget from './FaqWidget'
import Footer from './Footer'

import StartpageLogoRedesign from '../../../images/startpage-logo-redesign.svg'

const CONTAINER_STYLES = `
    width: 1200px;
    margin: auto;
    padding: 0 1rem;
    h2 {
        font-size: 48px;
        font-weight: 600;
    }
`

const ProtectionV2 = ({experiment, languageCode}) => {
    const site = useStaticQuery(graphql`
        query ProtectionV2 {
            site {
                siteMetadata {
                    ...ProtectionLanding
                }
            }
            imageSearchVariantEn: file(relativePath: {eq: "protection/en/search-focused-hero-image.png"}) {
                childImageSharp {
                    fixed(height: 377, quality: 100) {
                        ...GatsbyImageSharpFixed_noBase64
                    }
                }
            }
            imageSearchVariantDe: file(relativePath: {eq: "protection/de/search-focused-hero-image.png"}) {
                childImageSharp {
                    fixed(height: 377, quality: 100) {
                        ...GatsbyImageSharpFixed_noBase64
                    }
                }
            }
            imageBlockerVariantEn: file(relativePath: {eq: "protection/en/blocker-focused-hero-image.png"}) {
                childImageSharp {
                    fixed(height: 400, quality: 100) {
                        ...GatsbyImageSharpFixed_noBase64
                    }
                }
            }
            imageBlockerVariantDe: file(relativePath: {eq: "protection/de/blocker-focused-hero-image.png"}) {
                childImageSharp {
                    fixed(height: 400, quality: 100) {
                        ...GatsbyImageSharpFixed_noBase64
                    }
                }
            }
            imageSearch: file(relativePath: {eq: "protection/en/search-image.png"}) {
                childImageSharp {
                    fixed(height: 282, quality: 100) {
                        ...GatsbyImageSharpFixed_noBase64
                    }
                }
            }
            imageSearchDe: file(relativePath: {eq: "protection/de/search-image.png"}) {
                childImageSharp {
                    fixed(height: 282, quality: 100) {
                        ...GatsbyImageSharpFixed_noBase64
                    }
                }
            }
            imageCookies: file(relativePath: {eq: "protection/en/cookies-image.png"}) {
                childImageSharp {
                    fixed(height: 282, quality: 100) {
                        ...GatsbyImageSharpFixed_noBase64
                    }
                }
            }
            imageCookiesDe: file(relativePath: {eq: "protection/de/cookies-image.png"}) {
                childImageSharp {
                    fixed(height: 282, quality: 100) {
                        ...GatsbyImageSharpFixed_noBase64
                    }
                }
            }
            imageReport: file(relativePath: {eq: "protection/en/report-image.png"}) {
                childImageSharp {
                    fixed(height: 282, quality: 100) {
                        ...GatsbyImageSharpFixed_noBase64
                    }
                }
            }
            imageReportDe: file(relativePath: {eq: "protection/de/report-image.png"}) {
                childImageSharp {
                    fixed(height: 282, quality: 100) {
                        ...GatsbyImageSharpFixed_noBase64
                    }
                }
            }
            imageScore: file(relativePath: {eq: "protection/en/score-image.png"}) {
                childImageSharp {
                    fixed(height: 282, quality: 100) {
                        ...GatsbyImageSharpFixed_noBase64
                    }
                }
            }
            imageScoreDe: file(relativePath: {eq: "protection/de/score-image.png"}) {
                childImageSharp {
                    fixed(height: 282, quality: 100) {
                        ...GatsbyImageSharpFixed_noBase64
                    }
                }
            }
        }
    `)

    const homeLink = `https://www.startpage.com/${languageCode}`

    const localizedSideBySideData = {
        en: [
            {
                title: `Search privately, every time.`,
                description: `Unlike other search engines, we don’t track, log or sell your search history. And with our Anonymous View, you can access websites and browse privately without leaving a trace.`,
                image: site.imageSearch.childImageSharp.fixed,
            },
            {
                title: `No more creepy cookies.`,
                description: `We block over 1500 trackers & cookies but you control whether to allow specific domains and cookies as desired.`,
                image: site.imageCookies.childImageSharp.fixed,
            },
            {
                title: `Know the score.`,
                description: `To help you understand risk levels, each site you visit gets a privacy score between 1 and 5. Great privacy protections = high privacy score.`,
                image: site.imageScore.childImageSharp.fixed,
            },
            {
                title: `Measure your privacy.`,
                description: `Get your breakdown of the privacy scores of the sites you visit, and an overview of the total trackers and cookies. This data is stored in your browser and cannot be accessed by Startpage.`,
                image: site.imageReport.childImageSharp.fixed,
            },
        ],
        de: [
            {
                title: `Immer anonym suchen.`,
                description: `Im Gegensatz zu anderen Suchmaschinen verfolgen, protokollieren oder verkaufen wir deinen Suchverlauf nicht. Und mit unserer Anonymen Ansicht kannst du Webseiten völlig anonym besuchen, ohne Spuren zu hinterlassen.`,
                image: site.imageSearchDe.childImageSharp.fixed,
            },
            {
                title: `Nie wieder nervige Cookies.`,
                description: `Wir blockieren mehr als 1500 Tracker und Cookies, aber du hast die Kontrolle darüber, ob du bestimmte Domains und Cookies zulassen möchtest.`,
                image: site.imageCookiesDe.childImageSharp.fixed,
            },
            {
                title: `Behalte den Überblick.`,
                description: `Damit du die Risiken besser einschätzen kannst, erhält jede Website, die du besuchst, eine Datenschutzbewertung zwischen 1 und 5. Ein hoher Schutz der Privatsphäre = eine hohe Datenschutzbewertung.`,
                image: site.imageScoreDe.childImageSharp.fixed,
            },
            {
                title: `Berechne deine Privatsphäre.`,
                description: `Erhalte eine Aufschlüsselung der Datenschutzbewertungen der von dir besuchten Websites und einen Überblick über die Gesamtanzahl der Tracker und Cookies. Diese Daten werden in deinem Browser gespeichert und können von Startpage nicht eingesehen werden.`,
                image: site.imageReportDe.childImageSharp.fixed,
            },
        ],
    }

    // const sideBySideData = localizedSideBySideData[languageCode]

    const {
        ctaText,
        landingSection4Title,
        landingEulaLink,
        newsTagline,
        newsLogos,
        footerBlogAsset,
        footerLinks,
    } = site.site.siteMetadata.privacyprotect[languageCode]

    const [hasFiredVisit, setHasFiredVisit] = useState(false)
    const [installLink, setInstallLink] = useState(firefoxProtectionExtensionLink(languageCode))
    const [sideBySideData, setSideBySideData] = useState(localizedSideBySideData[languageCode])

    const setStoreLink = useCallback(() => {
        if (isChrome()) {
            setInstallLink(chromeProtectionExtensionLink(languageCode))
        } else {
            // firefox
            setInstallLink(firefoxProtectionExtensionLink(languageCode))
        }
    }, [languageCode])

    const reOrderSideBySide = useCallback(() => {
        const mutatedSideBySideData = [...sideBySideData]
        const tmp = mutatedSideBySideData[0]
        // eslint-disable-next-line prefer-destructuring
        mutatedSideBySideData[0] = mutatedSideBySideData[1]
        mutatedSideBySideData[1] = tmp
        setSideBySideData(mutatedSideBySideData)
    }, [sideBySideData])

    useEffect(() => {
        if (!hasFiredVisit) {
            setHasFiredVisit(true)
            setExtensionParamsToLocalStorage(experiment)
            logDplWithExtensionParams(EVENTS.StartpagePageLoad, {
                experiment,
            })
            logSgc()
            setStoreLink()
            if (isFirefox()) {
                reOrderSideBySide()
            }
        }
    }, [hasFiredVisit, experiment, setStoreLink, reOrderSideBySide])

    const translations = {
        en: {
            tagline: `Privacy Protection Extension`,
            faqSectionTitle: `Frequently Asked Questions`,
            ctaTextAlternate: `Download for free`,
            heroDescription1: `Get protected and take control of your personal data. Search privately and stop advertiser tracking with Startpage. It’s FREE!`,
            heroDescription2: `Downloading Privacy Protection will change your default search engine to startpage.com.`,
            section3Subtitle: `Startpage Privacy Protection extension never passes a person's IP address or collects personal data. The extension abides by our <a href="https://www.startpage.com/en/privacy-policy" target="_blank" rel="noopener noreferrer nofollow">Privacy Policy</a>. Additional browser-specific information can be found <a href="${installLink}" target="_blank" rel="noopener noreferrer nofollow">here</a>.`,
        },
        de: {
            tagline: `Privatsphäre-Schutz`,
            faqSectionTitle: `Häufig gestellte Fragen`,
            ctaTextAlternate: `Kostenlos herunterladen`,
            heroDescription1: `Übernimm die Kontrolle und schütze deine persönlichen Daten. Mit der anonymen Suche von Startpage haben Werbetreibende keine Chance, dich zu tracken. Gratis!`,
            heroDescription2: `Wenn du Privatsphäre-Schutz herunterlädst, wird deine Standardsuchmaschine auf startpage.com umgestellt.`,
            section3Subtitle: `Die Startpage Privatsphäre-Schutz Erweiterung gibt niemals die IP-Adresse einer Person weiter und sammelt keine persönliche Daten. Auch für die Erweiterung gilt unsere <a href="https://www.startpage.com/de/privacy-policy" target="_blank" rel="noopener noreferrer nofollow">Datenschutzrichtlinie</a>. Weiterführende browserspezifische Informationen gibt es <a href="${installLink}" target="_blank" rel="noopener noreferrer nofollow">hier</a>.`,
        },
    }

    const text = translations[languageCode]

    const heroData = {
        'protection-v2-search': {
            en: {
                title: `Say goodbye to your search history.`,
                heroImage: site.imageSearchVariantEn.childImageSharp.fixed,
            },
            de: {
                title: `Verabschiede dich von deinem Suchverlauf.`,
                heroImage: site.imageSearchVariantDe.childImageSharp.fixed,
            },
        },
        'protection-v2-blocker': {
            en: {
                title: `Block thousands of creepy hidden trackers.`,
                heroImage: site.imageBlockerVariantEn.childImageSharp.fixed,
            },
            de: {
                title: `Blockiert versteckte und unerwünschte Tracker.`,
                heroImage: site.imageBlockerVariantDe.childImageSharp.fixed,
            },
        },
        'protection-v2-both': {
            en: {
                title: `Search privately,<br>block trackers.`,
                heroImage: site.imageBlockerVariantEn.childImageSharp.fixed,
                bullet1: `Keep your search history private`,
                bullet2: `Block cookies and trackers`,
            },
            de: {
                title: `Anonym suchen, Tracker blockieren.`,
                heroImage: site.imageBlockerVariantDe.childImageSharp.fixed,
                bullet1: `Behalte deinen Suchverlauf für dich`,
                bullet2: `Blockiere Cookies und Tracker`,
            },
        },
    }

    const experimentVariantData = heroData[experiment][languageCode]

    const experimentVariantDescription = (experimentId) => {
        const {bullet1, bullet2} = experimentVariantData
        return (
            <div
                className="description"
                css={css`
                    p {
                        color: ${colors.midnight};
                        line-height: 24px;
                        margin: 0 0 1rem;
                        &:nth-last-of-type(1) {
                            margin: 0;
                        }
                    }
                `}
            >
                <p>{text.heroDescription1}</p>
                {experimentId === 'protection-v2-both' && (
                    <div className="bullet-container">
                        {[bullet1, bullet2].map((bulletText) => {
                            return (
                                <div
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        margin: 0 0 16px;
                                        svg {
                                            margin-right: 12px;
                                        }
                                    `}
                                    key={bulletText}
                                >
                                    <svg
                                        width="15"
                                        height="14"
                                        viewBox="0 0 15 14"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M15 2.88603L11.8376 0.717773L5.91014 8.52021L2.28111 6.02343L0 9.02943L6.77419 13.678V13.6616L6.79147 13.678L15 2.88603Z"
                                            fill={`${colors.teal}`}
                                        />
                                    </svg>
                                    <span className="bullet">{bulletText}</span>
                                </div>
                            )
                        })}
                    </div>
                )}
                <p>{text.heroDescription2}</p>
            </div>
        )
    }

    return (
        <section
            css={css`
                h1,
                h2,
                h3,
                h4,
                p,
                b,
                strong {
                    color: ${colors.midnight};
                }
                position: relative;
                overflow-x: hidden;
            `}
        >
            <section
                className="header"
                css={css`
                    color: ${colors.midnight};
                `}
            >
                <div
                    css={css`
                        ${CONTAINER_STYLES}
                        padding: 3rem 1rem 3rem;
                        background: ${colors.whisperLight};
                        display: flex;
                        align-items: center;
                        svg {
                            position: relative;
                            z-index: 1;
                            height: 25px;
                            width: 112px;
                            margin-right: 12px;
                        }
                        h2 {
                            font-size: 18px;
                            font-weight: 700;
                            margin-top: -9px;
                        }
                    `}
                >
                    <a href={homeLink} rel="noopener noreferrer">
                        <StartpageLogoRedesign />
                    </a>
                    <h2 className="tagline">{text.tagline}</h2>
                </div>
            </section>

            <section className="section-1">
                <div
                    css={css`
                        ${CONTAINER_STYLES}
                        position: relative;
                        display: flex;
                        justify-content: space-between;
                        padding: 0 1rem 4rem;
                        background: ${colors.whisperLight};
                        h1 {
                            font-size: 48px;
                            font-weight: 600;
                            margin: 0 0 25px;
                            color: ${colors.midnight};
                        }
                        .subtitle {
                            font-weight: 600;
                            color: ${colors.midnight};
                            margin-bottom: 2rem;
                            margin-top: 0.5rem;
                        }
                    `}
                >
                    <div
                        className="background-circle"
                        css={css`
                            height: 854px;
                            width: 854px;
                            position: absolute;
                            right: -35%;
                            top: -220px;
                            background: ${colors.tealLight};
                            border-radius: 50%;
                        `}
                    />
                    <div
                        className="intro-info"
                        css={css`
                            max-width: 550px;
                            .extension-text {
                                color: ${colors.brandBlue};
                                font-weight: 600;
                                font-size: 20px;
                            }
                        `}
                    >
                        <h1 dangerouslySetInnerHTML={createMarkup(experimentVariantData.title)} />

                        <div
                            css={css`
                                margin: 0 0 24px;
                            `}
                        >
                            <BrowserStoreWidget languageCode={languageCode} />
                        </div>

                        {experimentVariantDescription(experiment)}

                        <Button
                            text={ctaText}
                            ctaType="cta-1"
                            experiment={experiment}
                            languageCode={languageCode}
                            overrideStyles={css`
                                margin: 32px 0;
                            `}
                        />

                        <div
                            className="trusted-by-section"
                            css={css`
                                display: flex;
                                align-items: center;
                                .tagline {
                                    color: ${colors.midnight};
                                    margin: 0 1.75rem 0 0;
                                    line-height: 20px;
                                    ${languageCode === 'de' && 'flex: 0 0 25%;'}
                                }
                                img {
                                    margin: 0 1.5rem 0 0;
                                    max-width: 171px;
                                }
                            `}
                        >
                            <p className="tagline">{newsTagline}</p>
                            {newsLogos.map(({logo: {url = ``}}, i) => {
                                return <img key={i} src={convertDatoCmsImgPathsToLocal(url)} alt="news logo" />
                            })}
                        </div>
                    </div>
                    <div
                        className="app-preview-image-container"
                        css={css`
                            margin-top: 2rem;
                        `}
                    >
                        <Img fixed={experimentVariantData.heroImage} />
                    </div>
                </div>
            </section>

            <section
                className="section-2"
                css={css`
                    padding: 4rem 0 6rem;
                    background: ${colors.whisper};
                    position: relative;
                `}
            >
                <div
                    css={css`
                        ${CONTAINER_STYLES}
                        h2 {
                            margin: 0 0 2rem;
                            text-align: center;
                        }
                    `}
                >
                    <div>
                        {sideBySideData.map((item, i) => {
                            const {title, description, image} = item
                            const imageWidth = 54
                            return (
                                <div key={i}>
                                    <SideBySide
                                        gatsbyImage={image}
                                        title={title}
                                        description={description}
                                        imagePosition={i % 2 === 0 ? 'right' : 'left'}
                                        overrideStyles={css`
                                            h3 {
                                                font-size: 30px;
                                                font-weight: 600px;
                                            }
                                            .side-by-side-text {
                                                width: calc(100% - ${imageWidth}%);
                                            }
                                            .side-by-side-image {
                                                width: ${imageWidth}%;
                                            }
                                        `}
                                    />
                                </div>
                            )
                        })}
                    </div>
                </div>
            </section>

            <section
                className="section-3"
                css={css`
                    ${CONTAINER_STYLES}
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 4rem 1rem;
                    h2 {
                        margin: 0 0 2rem;
                        max-width: 49rem;
                        text-align: center;
                    }
                `}
            >
                {/* these divs are like a confeti texture in the bg */}
                <div
                    css={css`
                        height: 40px;
                        width: 40px;
                        border: 2px solid ${colors.teal};
                        position: absolute;
                        left: 7rem;
                        top: 13.6rem;
                    `}
                />
                <div
                    css={css`
                        height: 64px;
                        width: 64px;
                        border: 2px solid ${colors.gold};
                        position: absolute;
                        top: 4rem;
                        right: 6rem;
                    `}
                />
                <h2>{landingSection4Title}</h2>
                <div
                    dangerouslySetInnerHTML={createMarkup(text.section3Subtitle)}
                    css={css`
                        text-align: center;
                        margin: 0 0 2rem;
                        max-width: ${languageCode === 'de' ? '731px' : '776px'};
                        line-height: 25px;
                        font-size: 16px;
                        a {
                            color: ${colors.brandBlue};
                        }
                    `}
                />
                <Button
                    text={text.ctaTextAlternate}
                    ctaType="cta-2"
                    experiment={experiment}
                    languageCode={languageCode}
                />
            </section>

            <section
                className="section-4"
                css={css`
                    padding: 6rem 0 8rem;
                    background: ${colors.whisper};
                    h2 {
                        font-size: 46px;
                        margin: 0 0 4rem;
                        text-align: center;
                    }
                `}
            >
                <div
                    css={css`
                        ${CONTAINER_STYLES}
                    `}
                >
                    <h2>{text.faqSectionTitle}</h2>
                    <FaqWidget languageCode={languageCode} />
                </div>
            </section>

            <Footer
                languageCode={languageCode}
                links={footerLinks}
                footerBlogAsset={footerBlogAsset}
                additionalLinks={landingEulaLink}
                overrideStyles={css`
                    margin-top: 0;
                `}
            />
        </section>
    )
}

export default ProtectionV2
